import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export default function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('Processing...');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const handleCallback = async () => {
      try {
        // Check if there are hash params (Supabase OAuth puts tokens in URL hash)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');

        if (accessToken && refreshToken) {
          // Set the session from the hash tokens
          setStatus('Setting up session...');
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            console.error('Error setting session:', error);
            setStatus('Error: ' + error.message);
            setTimeout(() => navigate('/login', { replace: true }), 2000);
            return;
          }

          if (data.session) {
            // Store session in localStorage for persistence
            const sessionData = {
              access_token: data.session.access_token,
              refresh_token: data.session.refresh_token,
              user: data.session.user,
            };
            localStorage.setItem('bereket_session', JSON.stringify(sessionData));
            navigate('/dashboard', { replace: true });
            return;
          }
        }

        // If no hash params, check if session already exists
        setStatus('Checking session...');
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          navigate('/dashboard', { replace: true });
          return;
        }

        // Wait a moment for Supabase onAuthStateChange to fire
        setStatus('Waiting for authentication...');
        const timeout = setTimeout(() => {
          navigate('/login', { replace: true });
        }, 5000);

        // Listen for auth state change
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
          if (event === 'SIGNED_IN' && session) {
            clearTimeout(timeout);
            subscription.unsubscribe();
            navigate('/dashboard', { replace: true });
          }
        });

      } catch (err) {
        console.error('Auth callback error:', err);
        setStatus('Authentication failed');
        setTimeout(() => navigate('/login', { replace: true }), 2000);
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F5F4]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-[#57534E] font-body">{status}</p>
      </div>
    </div>
  );
}
